import { useState, useEffect, useCallback } from "react";
import * as Scrivito from "scrivito";
import { cloneDeep } from "lodash-es";
import { Element, scroller } from "react-scroll";
import setLanguage from "^/utils/setLanguage";
import I18n from "^/config/i18n";
import CoursesTable from "./CoursesTable";
import { Filter, FilteredBadges } from "./Filter";
import { degreeValues } from "../CoursePage/CoursePageObjClass";

import {
  parseWithIndexFormat,
  stringifyWithIndexFromat,
} from "../../utils/queryString";

const CourseOverviewPage = ({ page }) => {
  const [courses, setCourses] = useState([]);
  const [terms, setTerms] = useState({});
  const [sortBy, setSortBy] = useState(["title", "asc"]);
  const params = Scrivito.currentPageParams();
  const pageId = page.id();

  const getIsSelected = (attribute, item) =>
    terms[attribute] && terms[attribute].includes(item);

  const scrollTo = () => {
    scroller.scrollTo("scroll-to-table", {
      duration: 500,
      delay: 0,
    });
  };

  const updateUrlQuery = useCallback((termsToUse) => {
    Scrivito.navigateTo(() => Scrivito.currentPage(), {
      hash: "scroll-to-filter",
      q: stringifyWithIndexFromat(termsToUse),
    });
  }, []);

  const getCourses = useCallback((termsToUse, cb) => {
    Scrivito.load(() => Scrivito.currentPage()).then((cPage) => {
      const lang = cPage.language();
      Scrivito.load(() => {
        let search = Scrivito.Obj.onAllSites()
          .where("_objClass", "equals", "CoursePage")
          .and(["_language", "courseFilterLang"], "equals", lang)
          .andNot("hideOnCourseFinderPage", "equals", true);

        Object.keys(termsToUse).forEach((attrTerm) => {
          if (attrTerm === "degree") {
            const matchingDegrees = degreeValues.filter((degree) =>
              termsToUse.degree.some((prefix) => degree.startsWith(prefix))
            );
            search = search.and("degree", "equals", matchingDegrees);
          } else {
            search = search.and(attrTerm, "equals", termsToUse[attrTerm]);
          }
        });
        return search.order("title").take();
      }).then((allCourses) => {
        setCourses(allCourses);
        if (cb) {
          cb();
        }
      });
    });
  }, []);

  const updateTerms = (attr, item, isRemoveAll) => {
    const newTerms = isRemoveAll ? {} : cloneDeep(terms);

    if (!isRemoveAll) {
      const isSelected = getIsSelected(attr, item);

      if (!isSelected) {
        if (!newTerms[attr]) {
          newTerms[attr] = [];
        }
        newTerms[attr].push(item);
      } else {
        const index = newTerms[attr].indexOf(item);
        newTerms[attr].splice(index, 1);
        if (newTerms[attr].length < 1) {
          delete newTerms[attr];
        }
      }
    }

    setTerms(newTerms);
    getCourses(newTerms, () => updateUrlQuery(newTerms));
  };

  useEffect(() => {
    const newTerms = parseWithIndexFormat(params.q);
    setTerms(newTerms);
    getCourses(newTerms, () => updateUrlQuery(newTerms));
  }, [getCourses, pageId, params.q, updateUrlQuery]);

  const lang = page.language();
  setLanguage(page);

  return (
    <div className="course-overview">
      <div className="course-header bg-blue py-5">
        <div className="container">
          <Scrivito.ContentTag
            content={page}
            attribute="title"
            tag="h1"
            className="m-0"
          />
        </div>
      </div>
      <Element id="scroll-to-filter" />
      <div className="course-filter">
        <Scrivito.ContentTag
          content={page}
          attribute="introduction"
          className="container-left-only"
        />
        <div className="container">
          <Filter
            terms={terms}
            lang={lang}
            updateTerms={updateTerms}
            scrollTo={scrollTo}
          />
        </div>
      </div>
      <Element id="scroll-to-table" />

      <div className="container mt-3">
        <FilteredBadges terms={terms} updateTerms={updateTerms} />
        {courses.length < 1 ? (
          <div className="no-course-message w-100 text-center py-5">
            {I18n.t("noCoursesFoundMsg")}
          </div>
        ) : (
          <CoursesTable
            courses={courses}
            lang={lang}
            terms={terms}
            sortBy={sortBy}
            setSortBy={setSortBy}
          />
        )}
      </div>
    </div>
  );
};

export default Scrivito.provideComponent(
  "CourseOverviewPage",
  CourseOverviewPage
);
